import { Controller } from "@hotwired/stimulus";

// 5-step wizard for creating a Phase:
//   0. Identité   — nom, date_debut, date_fin (live duration helper)
//   1. Mission    — description_phase, description_mission_intervenant, lieux_mission
//   2. Équipe     — nombre_intervenant, jeh_par_intervenant
//   3. Finances   — remuneration_par_jeh (80–500), indemnisation_par_jeh
//                   (8–320), frais. Live preview of CA, rétribution, marge.
//   4. Récap      — synthesis + submit
//
// Validation runs on each `next()` and on submit. The form posts via plain
// Rails (no AJAX) so on success we redirect to the etude show page.
export default class extends Controller {
  static targets = [
    "form", "step", "progressStep", "progressCurrent", "progressTotal",
    "nextBtn", "prevBtn", "submitBtn", "error",
    // Step 0
    "nomField", "dateDebutField", "dateFinField",
    "dureeHint", "dureeValue",
    // Step 1
    "descPhaseField", "descMissionField", "lieuxField",
    // Step 2
    "nbIntervenantsField", "jehField", "totalJehDisplay",
    // Step 3
    "remunerationField", "indemnisationField", "fraisField",
    "caPreview", "retribPreview", "margePreview",
    "caFormula", "retribFormula", "margePct",
    // Récap
    "recapNom", "recapDates", "recapLieux", "recapEquipe", "recapJeh",
    "recapCa", "recapRetrib", "recapMarge", "recapDescription",
  ];

  static values = {
    current: { type: Number, default: 0 },
    total:   { type: Number, default: 5 },
  };

  connect() {
    this.progressTotalTarget.textContent = this.totalValue;
    this.updateFinanceLive();
    this.updateDuree();
    this._render();
  }

  // ---- Navigation ------------------------------------------------------

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= this.totalValue - 1) return;
    this.currentValue += 1;
    this._render();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  submit(event) {
    event.preventDefault();
    if (!this._validateAll()) return;
    this.submitBtnTarget.disabled = true;
    this.submitBtnTarget.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Création…';
    this.formTarget.submit();
  }

  // ---- Live computations ----------------------------------------------

  updateDuree() {
    const start = this.dateDebutFieldTarget.value;
    const end = this.dateFinFieldTarget.value;
    if (!start || !end) {
      this.dureeHintTarget.hidden = true;
      return;
    }
    const days = Math.round((new Date(end) - new Date(start)) / 86400000);
    if (days < 0) {
      this.dureeValueTarget.textContent = "Date de fin avant le début";
      this.dureeHintTarget.hidden = false;
      this.dureeHintTarget.classList.add("esm_wizard__hint--warn");
      return;
    }
    this.dureeHintTarget.classList.remove("esm_wizard__hint--warn");
    if (days === 0) {
      this.dureeValueTarget.textContent = "1 journée (même jour)";
    } else if (days < 7) {
      this.dureeValueTarget.textContent = `${days + 1} jours`;
    } else {
      const weeks = Math.round((days + 1) / 7);
      this.dureeValueTarget.textContent = `${days + 1} jours (${weeks} semaine${weeks > 1 ? "s" : ""})`;
    }
    this.dureeHintTarget.hidden = false;
  }

  updateFinanceLive() {
    const nb = this._num(this.nbIntervenantsFieldTarget?.value);
    const jeh = this._num(this.jehFieldTarget?.value);
    const remu = this._num(this.remunerationFieldTarget?.value);
    const indem = this._num(this.indemnisationFieldTarget?.value);
    const frais = this._num(this.fraisFieldTarget?.value);

    const totalJeh = nb * jeh;
    const ca = nb * remu * jeh + frais;
    const retrib = nb * indem * jeh;
    const marge = ca - retrib;
    const margePct = ca > 0 ? (marge / ca) * 100 : 0;

    if (this.hasTotalJehDisplayTarget) this.totalJehDisplayTarget.textContent = totalJeh;

    if (this.hasCaPreviewTarget)     this.caPreviewTarget.textContent     = this._fmt(ca);
    if (this.hasRetribPreviewTarget) this.retribPreviewTarget.textContent = this._fmt(retrib);
    if (this.hasMargePreviewTarget)  this.margePreviewTarget.textContent  = this._fmt(marge);

    if (this.hasCaFormulaTarget) {
      this.caFormulaTarget.textContent = nb > 0 && jeh > 0 && remu > 0
        ? `${nb} × ${jeh} JEH × ${this._fmt(remu)}${frais > 0 ? ` + ${this._fmt(frais)} frais` : ""}`
        : "";
    }
    if (this.hasRetribFormulaTarget) {
      this.retribFormulaTarget.textContent = nb > 0 && jeh > 0 && indem > 0
        ? `${nb} × ${jeh} JEH × ${this._fmt(indem)}` : "";
    }
    if (this.hasMargePctTarget) {
      this.margePctTarget.textContent = ca > 0 ? `${margePct.toFixed(1)} % du CA` : "";
    }
  }

  // ---- Validation ------------------------------------------------------

  _validateCurrent() {
    this._hideError();
    const step = this.currentValue;
    if (step === 0) {
      const nom = this.nomFieldTarget.value.trim();
      const debut = this.dateDebutFieldTarget.value;
      const fin = this.dateFinFieldTarget.value;
      if (!nom) return this._fail("Le nom de la phase est obligatoire.", this.nomFieldTarget);
      if (!debut) return this._fail("Précise la date de début.", this.dateDebutFieldTarget);
      if (!fin) return this._fail("Précise la date de fin.", this.dateFinFieldTarget);
      if (new Date(fin) < new Date(debut)) {
        return this._fail("La date de fin doit être postérieure (ou égale) à la date de début.", this.dateFinFieldTarget);
      }
      return true;
    }
    if (step === 1) {
      if (!this.descPhaseFieldTarget.value.trim()) {
        return this._fail("Décris la phase (objectifs, livrables).", this.descPhaseFieldTarget);
      }
      if (!this.descMissionFieldTarget.value.trim()) {
        return this._fail("Décris la mission de l'intervenant — c'est ce qui sera affiché aux candidats.", this.descMissionFieldTarget);
      }
      return true;
    }
    if (step === 2) {
      const nb = this._num(this.nbIntervenantsFieldTarget.value);
      const jeh = this._num(this.jehFieldTarget.value);
      if (nb < 1) return this._fail("Au moins 1 intervenant.", this.nbIntervenantsFieldTarget);
      if (jeh < 1) return this._fail("Au moins 1 JEH par intervenant.", this.jehFieldTarget);
      return true;
    }
    if (step === 3) {
      const remu = this._num(this.remunerationFieldTarget.value);
      const indem = this._num(this.indemnisationFieldTarget.value);
      const frais = this._num(this.fraisFieldTarget.value);
      if (this.remunerationFieldTarget.value === "") {
        return this._fail("Saisis la rémunération par JEH (0 si pro bono).", this.remunerationFieldTarget);
      }
      if (this.indemnisationFieldTarget.value === "") {
        return this._fail("Saisis l'indemnisation par JEH (0 si pro bono).", this.indemnisationFieldTarget);
      }
      if (remu !== 0 && (remu < 80 || remu > 500)) {
        return this._fail("Rémunération hors bornes : 80–500 € HT (ou 0 pour pro bono).", this.remunerationFieldTarget);
      }
      if (indem !== 0 && (indem < 8 || indem > 320)) {
        return this._fail("Indemnisation hors bornes : 8–320 € brut (ou 0 pour pro bono).", this.indemnisationFieldTarget);
      }
      if (frais < 0) return this._fail("Les frais doivent être positifs.", this.fraisFieldTarget);
      return true;
    }
    return true;
  }

  _validateAll() {
    for (let i = 0; i <= this.totalValue - 1; i += 1) {
      this.currentValue = i;
      this._render();
      if (!this._validateCurrent()) return false;
    }
    return true;
  }

  // ---- Rendering -------------------------------------------------------

  _render() {
    this.stepTargets.forEach((s) => {
      s.classList.toggle("esm_wizard__step--active", Number(s.dataset.step) === this.currentValue);
    });
    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    this.progressCurrentTarget.textContent = this.currentValue + 1;
    this.prevBtnTarget.hidden = this.currentValue === 0;

    const isLast = this.currentValue === this.totalValue - 1;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;

    if (isLast) this._fillRecap();
    this._hideError();
  }

  _fillRecap() {
    const nb = this._num(this.nbIntervenantsFieldTarget.value);
    const jeh = this._num(this.jehFieldTarget.value);
    const remu = this._num(this.remunerationFieldTarget.value);
    const indem = this._num(this.indemnisationFieldTarget.value);
    const frais = this._num(this.fraisFieldTarget.value);
    const ca = nb * remu * jeh + frais;
    const retrib = nb * indem * jeh;
    const marge = ca - retrib;
    const pct = ca > 0 ? ((marge / ca) * 100).toFixed(1) : "0";

    this.recapNomTarget.textContent = this.nomFieldTarget.value;
    const debut = this.dateDebutFieldTarget.value;
    const fin = this.dateFinFieldTarget.value;
    this.recapDatesTarget.textContent = (debut && fin)
      ? `${this._fmtDate(debut)} → ${this._fmtDate(fin)}`
      : "—";
    this.recapLieuxTarget.textContent = this.lieuxFieldTarget.value || "—";
    this.recapEquipeTarget.textContent = `${nb} × ${jeh} JEH/pers.`;
    this.recapJehTarget.textContent = `${nb * jeh} JEH`;
    this.recapCaTarget.textContent = this._fmt(ca);
    this.recapRetribTarget.textContent = this._fmt(retrib);
    this.recapMargeTarget.textContent = `${this._fmt(marge)} (${pct} %)`;
    this.recapDescriptionTarget.textContent = this.descPhaseFieldTarget.value || "—";
  }

  // ---- Helpers ---------------------------------------------------------

  _num(val) {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
  }

  _fmt(amount) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amount);
  }

  _fmtDate(iso) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  _fail(msg, focusEl) {
    this._showError(msg);
    focusEl?.focus();
    return false;
  }

  _showError(msg) {
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }
}
